import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import ReactDOM from 'react-dom';
import axios from "axios";
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import {
  FaSearch, FaPlus, FaEdit, FaTrash, FaCheck, FaTimes,
  FaFileExcel, FaFileDownload, FaCheckDouble, FaGift, FaHandHoldingUsd,
  FaClock, FaUndoAlt, FaFileInvoiceDollar, FaExclamationTriangle,
  FaUserCircle, FaCloudUploadAlt, FaSave, FaPaperPlane, FaMinus,
  FaTrophy, FaChartLine, FaCheckCircle, FaExclamationCircle,
  FaPlusCircle, FaMinusCircle, FaWallet
} from 'react-icons/fa';
import {
  commonSaveData,
  commonGetData,
  commonGetDataOne,
  commonGetDataTwo,
  commonGetDataFourth,
  initCommonState,
} from "../../../../redux/slices/commonSlice";
import {
  addAdvancePath,
  addIncentiveStaffPath,
  addBonusWagePath,
  addReimbursementStaffPath,
  addOtherDeductionsPath,
  getAdjustmentsListPath,
  getAdjustmentsSummaryPath,
  getAdjustmentsEmployeesPath,
  getAdjustmentAdvanceByStaffPath,
  approveAdjustmentPath,
  bulkApproveAdjustmentsPath,
  markAdjustmentsPayrollPath,
  deleteAdjustmentEntryPath,
  updateAdjustmentEntryPath,
  payrollProcessPath,
} from "../../../../services/config";
import { config } from "../../../../services/configHeader";
import { saveUpdateIncentive, initIncentiveState } from '../../../../redux/slices/incentiveSlice';
import './IncentivesAdvances.css';
import {
  getAdvanceStatusOptions, getIncentiveCategories, getAdvanceCategories,
  getBonusTypes, getDeductionTypes, getAdvanceBranches, getAdvanceDepartments,
  getMockEmployees,
} from '../../payrollDB';

// ─── Static lookup data ─────────────────────────────────────────────────────
const AI = {
  statusOptions:       getAdvanceStatusOptions(),
  incentiveCategories: getIncentiveCategories(),
  advanceCategories:   getAdvanceCategories(),
  bonusTypes:          getBonusTypes(),
  deductionTypes:      getDeductionTypes(),
  branches:            getAdvanceBranches(),
  departments:         getAdvanceDepartments(),
};

const PAYROLL_ADJUSTMENTS_HANDOFF_KEY = "payrollAdjustmentsHandoff";
const baseUrl = process.env.REACT_APP_BASE_URI;

// ─── Pure helpers (outside component — stable references) ────────────────────
const formatMonthLabel = (ym) => {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return "";
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleString("default", { month: "long", year: "numeric" });
};

const parseCurrencyString = (v, fallback = 0) => {
  if (typeof v === 'number') return v;
  if (!v) return fallback;
  const n = Number(String(v).replace(/[₹,\s]/g, ''));
  return Number.isFinite(n) ? n : fallback;
};

const formatCurrency = (val, fallback = '—') => {
  const n = Number(val);
  return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN')}` : fallback;
};

const formatSignedCurrency = (val, fallback = '—') => {
  if (val == null || Number.isNaN(Number(val))) return fallback;
  const n = Number(val);
  return `${n >= 0 ? '+' : '-'}${formatCurrency(Math.abs(n))}`;
};

const resolveGrossSalary = (e) =>
  Number(
    parseCurrencyString(e?.gross) ||
    e?.earnings?.grossSalary ||
    e?.grossSalary ||
    e?.salaryDetails?.grossAmount ||
    e?.salaryBreakup?.grossSalary ||
    e?.salaryBreakup?.data?.salaryOutput?.grossSalary ||
    0
  );

const resolveNetSalary = (e) =>
  Number(
    parseCurrencyString(e?.net) ||
    e?.netSalary ||
    e?.net_salary ||
    e?.salaryBreakup?.netSalary ||
    e?.salaryDetails?.netAmount ||
    0
  );

const normalizeAdjustmentRow = (row) => {
  const emp = row.employee || {};
  return {
    id:          row.id,
    staffId:     row.staffId ?? emp.staffId,
    employee: {
      id:         emp.id || row.employeeId || "",
      name:       emp.name || row.staffName || "—",
      avatar:     emp.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.name || "Employee")}&background=8660F8&color=fff`,
      department: emp.department ?? row.department ?? "—",
      branch:     emp.branch ?? row.branch ?? "—",
    },
    entryType:    row.entryType || "incentive",
    reason:       row.reason || "—",
    amount:       Number(row.amount) || 0,
    recoveryPlan: row.recoveryPlan || "—",
    date:         row.date || "—",
    createdBy:    row.createdBy || "—",
    status:       row.status || "pending",
    payrollMonth: row.payrollMonth || "—",
    notes:        row.notes || "",
  };
};

const normalizeTransactionsPayload = (payload) => {
  if (!payload) return [];
  const list = Array.isArray(payload) ? payload : payload.transactions || [];
  return list.map(normalizeAdjustmentRow);
};

// ─── Action-type ref constants (to distinguish mutations in commonSuccess) ───
const ACTION_APPROVE      = 'approve';
const ACTION_BULK_APPROVE = 'bulk_approve';
const ACTION_DELETE       = 'delete';
const ACTION_MARK_PAYROLL = 'mark_payroll';
const ACTION_SAVE_ENTRY   = 'save_entry';
const ACTION_SEND_PAYROLL = 'send_payroll';

// ────────────────────────────────────────────────────────────────────────────
const IncentivesAdvances = ({ onNavigate }) => {
  const user        = JSON.parse(localStorage.getItem('user_data')) || {};
  const dispatch    = useDispatch();
  const commonStore = useSelector((s) => s.common);
  const incentiveStore = useSelector((s) => s.incentive);
  const branchList     = useSelector((s) => s.branch?.brnGetData     || []);
  const departmentList = useSelector((s) => s.department?.depGetData || []);

  // ── AG Grid ref ──────────────────────────────────────────────────────────
  const gridRef = useRef(null);

  // ── Action tracking ref — tells the commonSuccess handler what just fired ─
  const pendingActionRef  = useRef(null);   // ACTION_* constant
  const payrollHandoffRef = useRef(null);   // payload for send-to-payroll

  // ── Date helpers ─────────────────────────────────────────────────────────
  const today        = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

  // ── Filters ───────────────────────────────────────────────────────────────
  const [activeTab,           setActiveTab]           = useState('all');
  const [searchQuery,         setSearchQuery]         = useState('');
  const [selectedMonth,       setSelectedMonth]       = useState(defaultMonth);
  const [selectedBranch,      setSelectedBranch]      = useState('');
  const [selectedDepartment,  setSelectedDepartment]  = useState('');
  const [selectedStatus,      setSelectedStatus]      = useState('all');
  const [selectedRows,        setSelectedRows]        = useState([]);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [transactions, setTransactions] = useState([]);
  const [kpis, setKpis] = useState({
    totalIncentives: 0, totalAdvances: 0, pendingApprovals: 0,
    totalRecoveries: 0, activeAdvances: 0, exceptions: 0,
  });
  const [employeesList, setEmployeesList] = useState(
    getMockEmployees().map((e) => ({
      staffId:     e.staffId,
      id:          e.employeeId,
      name:        e.staffName,
      department:  e.department,
      designation: e.jobTitle,
      branch:      e.branch,
      grossSalary: e.grossSalary,
      avatar:      e.avatar,
    }))
  );
  const [staffAdvanceSnapshot,   setStaffAdvanceSnapshot]   = useState(null);
  const [payrollEarningLoading,  setPayrollEarningLoading]  = useState(false);
  const [loading,                setLoading]                = useState(false);
  const [sendToPayrollLoading,   setSendToPayrollLoading]   = useState(false);

  // ── Modal ─────────────────────────────────────────────────────────────────
  const [showAddModal,              setShowAddModal]              = useState(false);
  const [editingEntryId,            setEditingEntryId]            = useState(null);
  const [selectedEntryType,         setSelectedEntryType]         = useState('incentive');
  const [selectedEmployeeForEntry,  setSelectedEmployeeForEntry]  = useState(null);
  const [employeeSearchTerm,        setEmployeeSearchTerm]        = useState('');

  // ── Form states ───────────────────────────────────────────────────────────
  const [incentiveData, setIncentiveData] = useState({
    category: 'Performance', amount: '', effectiveMonth: defaultMonth,
    recurring: 'One Time', taxable: true, description: '',
  });
  const [advanceData, setAdvanceData] = useState({
    category: 'Salary Advance', amount: '',
    disbursementDate: today.toISOString().split('T')[0],
    recoveryStartMonth: '', recoveryMethod: 'emi',
    installments: '5', allowEarlyClosure: true, approvalRequired: true, notes: '',
  });
  const [bonusData,     setBonusData]     = useState({ type: 'Festival Bonus', amount: '', effectiveMonth: defaultMonth, notes: '' });
  const [deductionData, setDeductionData] = useState({ type: 'Asset Damage', amount: '', recoveryMode: 'Current Payroll (Full)', remarks: '' });
  const [recoveryData,  setRecoveryData]  = useState({ advanceCase: '', recoveryAmount: '' });
  const [pettyCashData, setPettyCashData] = useState({ amount: '', date: today.toISOString().split('T')[0], purpose: '', notes: '' });
  const [internalComments, setInternalComments] = useState('');
  const [uploadedFile,  setUploadedFile]  = useState(null);

  // ── Derived ───────────────────────────────────────────────────────────────
  const getSelectedDate = () => `${selectedMonth}-01`;
  const monthLabel = useMemo(() => formatMonthLabel(selectedMonth), [selectedMonth]);

  // ── API calls ─────────────────────────────────────────────────────────────
  const loadTransactions = useCallback(() => {
    setLoading(true);
    dispatch(commonGetData(`${getAdjustmentsListPath}?selectedMonth=${selectedMonth}&userId=${user.user_id}`));
  }, [dispatch, selectedMonth, user.user_id]);

  const loadSummary = useCallback(() => {
    dispatch(commonGetDataOne(`${getAdjustmentsSummaryPath}?selectedMonth=${selectedMonth}&userId=${user.user_id}`));
  }, [dispatch, selectedMonth, user.user_id]);

  const loadEmployeeList = useCallback(() => {
    dispatch(commonGetDataFourth(`${getAdjustmentsEmployeesPath}?userId=${user.user_id}`));
  }, [dispatch, user.user_id]);

  const fetchEmployeeSnapshot = useCallback(async (staffIdForRequest) => {
    if (!user.user_id || !baseUrl) return null;
    try {
      const res = await axios.post(`${baseUrl}${payrollProcessPath}`, {
        action: "fetchEmployeeSnapshot",
        userId: Number(user.user_id),
        staffId: Number(staffIdForRequest),
      }, config);
      return res.data?.data || null;
    } catch (err) {
      console.error("[Advance] fetchEmployeeSnapshot error:", err);
      return null;
    }
  }, [user.user_id]);

  const fetchRunPayrollQueueData = useCallback(async (staffIdForRequest) => {
    if (!user.user_id || !baseUrl) return null;
    try {
      const res = await axios.post(`${baseUrl}${payrollProcessPath}`, {
        action: "fetchRunPayrollQueue",
        userId: Number(user.user_id),
        selectedMonth,
      }, config);
      const staffDetails = res.data?.data?.staffDetails || [];
      return staffDetails.find((e) => Number(e.staffId) === Number(staffIdForRequest)) || null;
    } catch (err) {
      console.error("[Advance] fetchRunPayrollQueue error:", err);
      return null;
    }
  }, [user.user_id, selectedMonth]);

  const loadEarningDetails = useCallback(async (staffIdForRequest) => {
    if (!user.user_id || !baseUrl) return;
    setPayrollEarningLoading(true);
    try {
      const [snapshotData, queueData] = await Promise.all([
        fetchEmployeeSnapshot(staffIdForRequest),
        fetchRunPayrollQueueData(staffIdForRequest),
      ]);

      if (queueData) {
        const grossAmount      = parseCurrencyString(queueData.gross);
        const netAmount        = parseCurrencyString(queueData.net);
        const deductionsAmount = Math.abs(parseCurrencyString(queueData.deductions));

        const combinedEmployee = {
          staffId:      queueData.staffId,
          id:           queueData.id || queueData.employeeId,
          name:         queueData.name || queueData.staffName || queueData.employeeName || "Employee",
          department:   queueData.dept || queueData.department || "",
          designation:  queueData.role || queueData.designation || queueData.jobTitle || "",
          branch:       queueData.branch || "",
          payrollMonth: selectedMonth,
          grossSalary:  grossAmount,
          netSalary:    netAmount,
          deductions:   deductionsAmount,
          avatar:       queueData.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(queueData.name || "Employee")}&background=8660F8&color=fff`,
          basic:        queueData.basic,
          allowances:   queueData.allowances,
          gross:        queueData.gross,
          net:          queueData.net,
          snapshotData: snapshotData || {},
        };

        setEmployeesList((prev) =>
          prev.map((emp) =>
            Number(emp.staffId) === Number(combinedEmployee.staffId)
              ? { ...emp, ...combinedEmployee }
              : emp
          )
        );

        // FIX: Update selectedEmployeeForEntry with fresh data if it matches
        setSelectedEmployeeForEntry((prev) => {
          if (!prev) return prev;
          if (Number(prev.staffId) === Number(combinedEmployee.staffId)) {
            return { ...prev, ...combinedEmployee };
          }
          return prev;
        });

        setStaffAdvanceSnapshot(snapshotData);
      }
    } catch (err) {
      console.error("[Advance] loadEarningDetails error:", err);
      toast.error(err?.response?.data?.message || "Failed to load earning details");
    } finally {
      setPayrollEarningLoading(false);
    }
  }, [selectedMonth, user.user_id, fetchEmployeeSnapshot, fetchRunPayrollQueueData]);

  // ── On mount / filter change ──────────────────────────────────────────────
  useEffect(() => {
    loadTransactions();
    loadSummary();
    loadEmployeeList();
    loadEarningDetails(user.staffId || 1);
  }, [selectedMonth, selectedBranch]); // eslint-disable-line

  // ── Load staff advance snapshot when employee is selected ────────────────
  useEffect(() => {
    if (selectedEmployeeForEntry?.staffId) {
      dispatch(
        commonGetDataTwo(
          `${getAdjustmentAdvanceByStaffPath(selectedEmployeeForEntry.staffId)}?userId=${user.user_id}`
        )
      );
    }
  }, [dispatch, selectedEmployeeForEntry?.staffId, user.user_id]); // eslint-disable-line

  // ── Redux GET response handler ────────────────────────────────────────────
  useEffect(() => {
    if (commonStore.commonGetSuccess) {
      if (commonStore.commonGetData != null) {
        setTransactions(normalizeTransactionsPayload(commonStore.commonGetData));
      }
      setLoading(false);
    }
    if (commonStore.commonGetError) {
      if (commonStore.commonGetData == null) setTransactions([]);
      setLoading(false);
    }

    if (commonStore.commonGetOneSuccess && commonStore.commonGetOneData) {
      setKpis({
        totalIncentives:  commonStore.commonGetOneData.totalIncentives  ?? 0,
        totalAdvances:    commonStore.commonGetOneData.totalAdvances     ?? 0,
        pendingApprovals: commonStore.commonGetOneData.pendingApprovals  ?? 0,
        totalRecoveries:  commonStore.commonGetOneData.totalRecoveries   ?? 0,
        activeAdvances:   commonStore.commonGetOneData.activeAdvances    ?? 0,
        exceptions:       commonStore.commonGetOneData.exceptions        ?? 0,
      });
    }

    if (commonStore.commonGetTwoSuccess && commonStore.commonGetTwoData) {
      setStaffAdvanceSnapshot(commonStore.commonGetTwoData);
    }

    if (commonStore.commonGetFourthSuccess && commonStore.commonGetFourthData) {
      const list = Array.isArray(commonStore.commonGetFourthData) ? commonStore.commonGetFourthData : [];
      setEmployeesList((prev) => {
        const mapped = list.map((e) => {
          const existing = prev.find((emp) => Number(emp.staffId) === Number(e.staffId));
          const base = {
            staffId:     e.staffId,
            id:          e.id || e.employeeId,
            name:        e.name || e.staffName,
            department:  e.department || "",
            designation: e.designation || e.jobTitle || "",
            branch:      e.branch || "",
            grossSalary: e.grossSalary ?? e.gross ?? e.salaryDetails?.grossAmount ?? e.salaryBreakup?.grossSalary ?? 0,
            netSalary:   e.netSalary ?? e.net_salary ?? e.salaryBreakup?.netSalary ?? e.salaryDetails?.netAmount ?? null,
            deductions:  typeof e.deductions === 'number' ? e.deductions : typeof e.totalDeductions === 'number' ? e.totalDeductions : 0,
            avatar:      e.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(e.name || "Employee")}&background=8660F8&color=fff`,
          };
          if (!existing?.payrollMonth) return base;
          return {
            ...base,
            payrollMonth:      existing.payrollMonth,
            earnings:          existing.earnings,
            payrollDeductions: existing.payrollDeductions,
            attendance:        existing.attendance,
            payrollStatus:     existing.payrollStatus,
            grossSalary:       existing.grossSalary,
            netSalary:         existing.netSalary,
            deductions:        existing.deductions,
            snapshotData:      existing.snapshotData,
          };
        });
        return mapped;
      });
    }
  }, [
    commonStore.commonGetSuccess, commonStore.commonGetData, commonStore.commonGetError,
    commonStore.commonGetOneSuccess, commonStore.commonGetOneData,
    commonStore.commonGetTwoSuccess, commonStore.commonGetTwoData,
    commonStore.commonGetFourthSuccess, commonStore.commonGetFourthData,
  ]);

  // ── Redux POST/mutation response handler ──────────────────────────────────
  // FIX: Uses pendingActionRef to distinguish approve / delete / save / etc.
  useEffect(() => {
    if (commonStore.commonSuccess) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;

      if (action === ACTION_SEND_PAYROLL && payrollHandoffRef.current) {
        // Send to payroll succeeded → navigate
        sessionStorage.setItem(
          PAYROLL_ADJUSTMENTS_HANDOFF_KEY,
          JSON.stringify(payrollHandoffRef.current)
        );
        payrollHandoffRef.current = null;
        setSendToPayrollLoading(false);
        toast.success(commonStore.commonMessage || "Adjustments marked for payroll");
        onNavigate?.("reimbursement");

      } else if (action === ACTION_APPROVE || action === ACTION_BULK_APPROVE) {
        // Approve succeeded → refresh data but do NOT close modal or reset forms
        toast.success(commonStore.commonMessage || "Approved successfully");
        loadTransactions();
        loadSummary();

      } else if (action === ACTION_DELETE) {
        // Delete succeeded → refresh data, do NOT close modal
        toast.success(commonStore.commonMessage || "Entry deleted");
        loadTransactions();
        loadSummary();

      } else if (action === ACTION_MARK_PAYROLL) {
        // Mark for payroll succeeded → just refresh
        toast.success(commonStore.commonMessage || "Marked for payroll");
        loadTransactions();
        loadSummary();

      } else {
        // Save / update entry succeeded → close modal
        toast.success(commonStore.commonMessage || "Saved successfully");
        loadTransactions();
        loadSummary();
        setShowAddModal(false);
        setEditingEntryId(null);
      }

      dispatch(initCommonState());
    }

    if (commonStore.commonError) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;

      if (action === ACTION_SEND_PAYROLL) {
        payrollHandoffRef.current = null;
        setSendToPayrollLoading(false);
      }
      toast.error(commonStore.commonMessage || "Something went wrong");
      dispatch(initCommonState());
    }
  }, [commonStore.commonSuccess, commonStore.commonError]); // eslint-disable-line

  // ── Incentive slice response handler ─────────────────────────────────────
  useEffect(() => {
    if (incentiveStore?.incSuccess) {
      toast.success(incentiveStore.incMessage || "Incentive added");
      loadTransactions();
      loadSummary();
      setShowAddModal(false);
      setEditingEntryId(null);
      dispatch(initIncentiveState());
    }
    if (incentiveStore?.incError) {
      toast.error(incentiveStore.incMessage || "Failed to add incentive");
      dispatch(initIncentiveState());
    }
  }, [incentiveStore?.incSuccess, incentiveStore?.incError]); // eslint-disable-line

  // ── KPI recompute from local transactions (fallback when server summary absent) ──
  useEffect(() => {
    if (commonStore.commonGetOneSuccess && commonStore.commonGetOneData) return;
    if (!Array.isArray(transactions)) return;
    const sums = transactions.reduce((acc, t) => {
      const amt  = Number(t.amount) || 0;
      const type = (t.entryType || '').toLowerCase();
      if (type === 'incentive') acc.totalIncentives += amt;
      if (type === 'advance')   acc.totalAdvances   += amt;
      if (type === 'recovery')  acc.totalRecoveries += amt;
      if (t.status === 'pending')                                             acc.pendingApprovals += 1;
      if (type === 'advance' && (t.status === 'approved' || t.status === 'processed')) acc.activeAdvances += 1;
      if (t.status === 'rejected' || t.status === 'error')                   acc.exceptions += 1;
      return acc;
    }, { totalIncentives: 0, totalAdvances: 0, totalRecoveries: 0, pendingApprovals: 0, activeAdvances: 0, exceptions: 0 });
    setKpis((prev) => ({ ...prev, ...sums }));
  }, [transactions, commonStore.commonGetOneSuccess, commonStore.commonGetOneData]);

  // ── Filtered data ─────────────────────────────────────────────────────────
  const filteredEmployees = useMemo(() =>
    employeesList.filter((emp) =>
      String(emp.name || "").toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
      String(emp.id   || "").toLowerCase().includes(employeeSearchTerm.toLowerCase())
    ), [employeesList, employeeSearchTerm]);

  const filteredTransactions = useMemo(() => {
    let f = Array.isArray(transactions) ? [...transactions] : [];
    if (activeTab !== "all") {
      const apiType = activeTab === "petty-cash" ? "petty_cash" : activeTab;
      f = f.filter((t) => t.entryType === apiType);
    }
    if (searchQuery) f = f.filter((t) =>
      (t.employee?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.employee?.id   || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (selectedStatus !== 'all') f = f.filter((t) => t.status === selectedStatus);
    if (selectedBranch) f = f.filter((t) =>
      (t.employee?.branch || '').toLowerCase().includes(selectedBranch.toLowerCase())
    );
    if (selectedDepartment) f = f.filter((t) =>
      (t.employee?.department || '').toLowerCase().includes(selectedDepartment.toLowerCase())
    );
    return f;
  }, [transactions, activeTab, searchQuery, selectedStatus, selectedBranch, selectedDepartment]);

  // ── EMI preview ───────────────────────────────────────────────────────────
  const emiPreview = useMemo(() => {
    const amount       = parseFloat(advanceData.amount)       || 0;
    const installments = parseInt(advanceData.installments)   || 1;
    return installments > 0 ? amount / installments : 0;
  }, [advanceData.amount, advanceData.installments]);

  const selectedEmployeeDetails = selectedEmployeeForEntry || employeesList[0];

  // ── Net impact ────────────────────────────────────────────────────────────
  const netImpact = useMemo(() => {
    if (!selectedEmployeeForEntry) return { currentNet: 0, newAmount: 0, projectedNet: 0 };
    const currentGross = resolveGrossSalary(selectedEmployeeForEntry);
    const currentNet   = resolveNetSalary(selectedEmployeeForEntry) || (currentGross - Number(selectedEmployeeForEntry.deductions || 0));
    let newAmount = 0;
    if (selectedEntryType === 'incentive') newAmount = parseFloat(incentiveData.amount) || 0;
    else if (selectedEntryType === 'bonus') newAmount = parseFloat(bonusData.amount) || 0;
    else if (selectedEntryType === 'advance' && advanceData.recoveryMethod === 'emi') newAmount = -emiPreview;
    else if (selectedEntryType === 'deduction') newAmount = -(parseFloat(deductionData.amount) || 0);
    else if (selectedEntryType === 'recovery')  newAmount = -(parseFloat(recoveryData.recoveryAmount) || 0);
    return { currentNet, newAmount, projectedNet: currentNet + newAmount };
  }, [selectedEmployeeForEntry, selectedEntryType, incentiveData.amount, bonusData.amount, advanceData.recoveryMethod, emiPreview, deductionData.amount, recoveryData.recoveryAmount]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSelectAll = (e) => {
    if (!gridRef.current?.api) return;
    e.target.checked ? gridRef.current.api.selectAll() : gridRef.current.api.deselectAll();
  };

  const handleAddEntry = () => {
    setEditingEntryId(null);
    setSelectedEmployeeForEntry(null);
    setEmployeeSearchTerm('');
    setSelectedEntryType('incentive');
    setIncentiveData({ category: 'Performance', amount: '', effectiveMonth: defaultMonth, recurring: 'One Time', taxable: true, description: '' });
    setAdvanceData({ category: 'Salary Advance', amount: '', disbursementDate: today.toISOString().split('T')[0], recoveryStartMonth: '', recoveryMethod: 'emi', installments: '5', allowEarlyClosure: true, approvalRequired: true, notes: '' });
    setBonusData({ type: 'Festival Bonus', amount: '', effectiveMonth: defaultMonth, notes: '' });
    setDeductionData({ type: 'Asset Damage', amount: '', recoveryMode: 'Current Payroll (Full)', remarks: '' });
    setRecoveryData({ advanceCase: '', recoveryAmount: '' });
    setPettyCashData({ amount: '', date: today.toISOString().split('T')[0], purpose: '', notes: '' });
    setInternalComments('');
    setUploadedFile(null);
    loadEmployeeList();
    setShowAddModal(true);
  };

  // FIX: When employee is selected in modal, immediately set them and load their data
  const handleEmployeeSelectForEntry = useCallback((emp) => {
    setSelectedEmployeeForEntry(emp);
    setStaffAdvanceSnapshot(null);
    if (emp?.staffId) {
      loadEarningDetails(emp.staffId);
    }
  }, [loadEarningDetails]);

  // FIX: handleEditRow — Number() coercion for staffId + loadEarningDetails called
  const handleEditRow = useCallback((row) => {
    // Use Number() to handle string/number staffId mismatch
    const emp = employeesList.find((e) => Number(e.staffId) === Number(row.staffId)) || null;

    setEditingEntryId(row.id);
    setSelectedEntryType(row.entryType === 'petty_cash' ? 'petty-cash' : (row.entryType || 'incentive'));
    setInternalComments('');

    // Pre-fill form fields from row data
    if (row.entryType === 'incentive') {
      setIncentiveData((prev) => ({
        ...prev,
        category:    row.reason       || 'Performance',
        amount:      String(row.amount || ''),
        description: row.notes        || '',
      }));
    } else if (row.entryType === 'advance') {
      setAdvanceData((prev) => ({
        ...prev,
        category: row.reason        || 'Salary Advance',
        amount:   String(row.amount || ''),
        notes:    row.notes         || '',
      }));
    } else if (row.entryType === 'bonus') {
      setBonusData((prev) => ({
        ...prev,
        type:   row.reason        || 'Festival Bonus',
        amount: String(row.amount || ''),
        notes:  row.notes         || '',
      }));
    } else if (row.entryType === 'deduction') {
      setDeductionData((prev) => ({
        ...prev,
        type:    row.reason        || '',
        amount:  String(row.amount || ''),
        remarks: row.notes         || '',
      }));
    } else if (row.entryType === 'recovery') {
      setRecoveryData((prev) => ({
        ...prev,
        recoveryAmount: String(row.amount || ''),
      }));
      setInternalComments(row.notes || row.reason || '');
    } else if (row.entryType === 'petty_cash') {
      setPettyCashData((prev) => ({
        ...prev,
        amount: String(row.amount || ''),
        notes:  row.notes || '',
      }));
    }

    // Set the employee — if found in list, use it; otherwise build a minimal object from row
    const resolvedEmp = emp || {
      staffId:     row.staffId,
      id:          row.employee?.id   || row.employeeId || '',
      name:        row.employee?.name || row.staffName  || 'Employee',
      department:  row.employee?.department || '—',
      designation: '—',
      branch:      row.employee?.branch || '—',
      grossSalary: 0,
      avatar:      row.employee?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(row.employee?.name || 'Employee')}&background=8660F8&color=fff`,
    };

    setSelectedEmployeeForEntry(resolvedEmp);
    setEmployeeSearchTerm(resolvedEmp.name || '');

    // FIX: Load payroll snapshot for this employee
    if (resolvedEmp.staffId) {
      loadEarningDetails(resolvedEmp.staffId);
    }

    setShowAddModal(true);
  }, [employeesList, loadEarningDetails]);

  // FIX: handleApproveRow — sets pendingActionRef before dispatch; optimistic update kept
  const handleApproveRow = useCallback((advanceId) => {
    const row = transactions.find((t) => t.id === advanceId);

    // Optimistic UI update
    if (row && row.status !== 'approved') {
      setTransactions((prev) =>
        prev.map((t) => t.id === advanceId ? { ...t, status: 'approved' } : t)
      );
      setKpis((prev) => {
        const pending = Math.max(0, (prev.pendingApprovals || 0) - 1);
        return {
          ...prev,
          pendingApprovals: pending,
          totalIncentives: (row.entryType === 'incentive') ? Number(prev.totalIncentives || 0) + Number(row.amount || 0) : prev.totalIncentives,
          totalAdvances:   (row.entryType === 'advance')   ? Number(prev.totalAdvances   || 0) + Number(row.amount || 0) : prev.totalAdvances,
          totalRecoveries: (row.entryType === 'recovery')  ? Number(prev.totalRecoveries || 0) + Number(row.amount || 0) : prev.totalRecoveries,
          activeAdvances:  (row.entryType === 'advance')   ? Number(prev.activeAdvances  || 0) + 1 : prev.activeAdvances,
        };
      });
    }

    // Tag this action so success handler knows NOT to close modal
    pendingActionRef.current = ACTION_APPROVE;

    dispatch(commonSaveData({
      path: `${approveAdjustmentPath(advanceId)}?userId=${user.user_id}`,
      data: { id: advanceId, entryType: row?.entryType || row?.type || "advance" },
    }));
  }, [dispatch, user.user_id, transactions]);

  // FIX: handleDeleteRow — sets pendingActionRef
  const handleDeleteRow = useCallback((row) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    pendingActionRef.current = ACTION_DELETE;
    dispatch(commonSaveData({
      path: `${deleteAdjustmentEntryPath(row.id)}?userId=${user.user_id}`,
      method: "delete",
      data: { entryType: row.entryType || "advance" },
    }));
  }, [dispatch, user.user_id]);

  // FIX: handleBulkApprove — sets pendingActionRef
  const handleBulkApprove = useCallback(() => {
    if (!selectedRows.length) { toast.error("Select at least one entry"); return; }
    pendingActionRef.current = ACTION_BULK_APPROVE;
    dispatch(commonSaveData({
      path: bulkApproveAdjustmentsPath,
      data: { userId: user.user_id, advanceIds: selectedRows },
    }));
  }, [dispatch, selectedRows, user.user_id]);

  // FIX: handleMarkForPayroll — sets pendingActionRef
  const handleMarkForPayroll = useCallback(() => {
    if (!selectedRows.length) { toast.error("Select at least one entry"); return; }
    pendingActionRef.current = ACTION_MARK_PAYROLL;
    dispatch(commonSaveData({
      path: markAdjustmentsPayrollPath,
      data: { userId: user.user_id, advanceIds: selectedRows, payrollMonth: getSelectedDate() },
    }));
  }, [dispatch, selectedRows, user.user_id, selectedMonth]); // eslint-disable-line

  const handleSendToPayroll = useCallback(() => {
    const rows = selectedRows.length
      ? filteredTransactions.filter((t) => selectedRows.includes(t.id) && t.status === "approved")
      : filteredTransactions.filter((t) => t.status === "approved");

    if (!rows.length) {
      toast.error(
        selectedRows.length
          ? "Selected entries must be approved before sending to payroll"
          : "No approved adjustments for this month."
      );
      return;
    }

    payrollHandoffRef.current = {
      source: "advance-incentive",
      selectedMonth,
      monthLabel: monthLabel || selectedMonth,
      summary: kpis,
      adjustments: rows,
    };

    pendingActionRef.current = ACTION_SEND_PAYROLL;
    setSendToPayrollLoading(true);
    dispatch(commonSaveData({
      path: markAdjustmentsPayrollPath,
      data: { userId: user.user_id, advanceIds: rows.map((r) => r.id), payrollMonth: getSelectedDate() },
    }));
  }, [dispatch, filteredTransactions, selectedRows, selectedMonth, monthLabel, kpis, user.user_id]); // eslint-disable-line

  // FIX: handleSubmitEntry — sets pendingActionRef = ACTION_SAVE_ENTRY
  const handleSubmitEntry = () => {
    if (!selectedEmployeeForEntry) { toast.error('Please select an employee'); return; }

    const staffId       = selectedEmployeeForEntry.staffId;
    const selectedDate  = getSelectedDate();
    const apiEntryType  = selectedEntryType === 'petty-cash' ? 'petty_cash' : selectedEntryType;

    if (editingEntryId) {
      let amount = '';
      let notes  = '';
      switch (selectedEntryType) {
        case 'incentive':  amount = incentiveData.amount;  notes = incentiveData.description; break;
        case 'advance':    amount = advanceData.amount;    notes = advanceData.notes;          break;
        case 'bonus':      amount = bonusData.amount;      notes = bonusData.notes;            break;
        case 'deduction':  amount = deductionData.amount;  notes = deductionData.remarks;      break;
        case 'recovery':   amount = recoveryData.recoveryAmount; notes = internalComments;     break;
        case 'petty-cash': amount = pettyCashData.amount;  notes = pettyCashData.notes;        break;
        default: break;
      }
      if (!amount) { toast.error('Please enter amount'); return; }

      pendingActionRef.current = ACTION_SAVE_ENTRY;
      dispatch(commonSaveData({
        path: `${updateAdjustmentEntryPath(editingEntryId)}?userId=${user.user_id}`,
        method: 'put',
        data: { staffId, entryType: apiEntryType, amount: Number(amount), effectiveMonth: monthLabel || selectedMonth, notes },
      }));
      return;
    }

    // New entry
    pendingActionRef.current = ACTION_SAVE_ENTRY;

    switch (selectedEntryType) {
      case 'incentive':
        if (!incentiveData.amount) { toast.error('Please enter incentive amount'); pendingActionRef.current = null; return; }
        dispatch(saveUpdateIncentive({
          path: addIncentiveStaffPath,
          data: {
            userId: user.user_id, staffId,
            type: incentiveData.category, amount: incentiveData.amount,
            incentiveDate: incentiveData.effectiveMonth ? `${incentiveData.effectiveMonth}-01` : selectedDate,
            notes: incentiveData.description,
          },
        }));
        break;

      case 'advance':
        if (!advanceData.amount) { toast.error('Please enter advance amount'); pendingActionRef.current = null; return; }
        dispatch(commonSaveData({
          path: addAdvancePath,
          data: {
            userId: user.user_id, staffId,
            type: advanceData.category,
            amount: advanceData.amount,
            paymentDate: advanceData.disbursementDate || selectedDate,
            recoveryStartDate: advanceData.recoveryStartMonth ? `${advanceData.recoveryStartMonth}-01` : '',
            installments: advanceData.installments, notes: advanceData.notes,
          },
        }));
        break;

      case 'bonus':
        if (!bonusData.amount) { toast.error('Please enter bonus amount'); pendingActionRef.current = null; return; }
        dispatch(commonSaveData({
          path: addBonusWagePath,
          data: { userId: user.user_id, staffId, type: bonusData.type, amount: bonusData.amount, paymentDate: selectedDate, notes: bonusData.notes },
        }));
        break;

      case 'deduction':
        if (!deductionData.amount) { toast.error('Please enter deduction amount'); pendingActionRef.current = null; return; }
        dispatch(commonSaveData({
          path: addOtherDeductionsPath,
          data: { userId: user.user_id, staffId, type: deductionData.type, amount: deductionData.amount, paymentDate: selectedDate, notes: deductionData.remarks },
        }));
        break;

      case 'recovery':
        if (!recoveryData.recoveryAmount) { toast.error('Please enter recovery amount'); pendingActionRef.current = null; return; }
        if (Number(recoveryData.recoveryAmount) > Number(staffAdvanceSnapshot?.totalOutstanding || 0)) { toast.error('Recovery amount cannot exceed outstanding advance'); pendingActionRef.current = null; return; }
        dispatch(commonSaveData({
          path: addOtherDeductionsPath,
          data: {
            userId: user.user_id, staffId,
            type: 'Advance Recovery',
            amount: recoveryData.recoveryAmount,
            paymentDate: selectedDate,
            notes: [recoveryData.advanceCase, internalComments].filter(Boolean).join(' - '),
          },
        }));
        break;

      case 'petty-cash':
        if (!pettyCashData.amount) { toast.error('Please enter petty cash amount'); pendingActionRef.current = null; return; }
        if (!pettyCashData.purpose) { toast.error('Please select petty cash type'); pendingActionRef.current = null; return; }
        dispatch(commonSaveData({
          path: addReimbursementStaffPath,
          data: {
            userId: user.user_id, staffId,
            amount: pettyCashData.amount,
            paymentDate: pettyCashData.date || selectedDate,
            notes: [pettyCashData.purpose, pettyCashData.notes].filter(Boolean).join(' - '),
          },
        }));
        break;

      default:
        pendingActionRef.current = null;
        break;
    }
  };

  const onSelectionChanged = useCallback(() => {
    if (gridRef.current?.api) {
      setSelectedRows(gridRef.current.api.getSelectedRows().map((r) => r.id));
    }
  }, []);

  // ── AG Grid column definitions ─────────────────────────────────────────────
  // FIX: columnDefs recreated when action handlers change (added to deps array)
  const columnDefs = useMemo(() => [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 44, minWidth: 44, maxWidth: 44,
      pinned: 'left', resizable: false, sortable: false, filter: false,
    },
    {
      headerName: 'Employee', field: 'employee', minWidth: 170, flex: 2,
      cellRenderer: ({ value: emp }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '100%' }}>
          <img src={emp.avatar} alt={emp.name} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.2 }}>{emp.name}</div>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{emp.id}</div>
          </div>
        </div>
      ),
    },
    {
      headerName: 'Department', field: 'employee', colId: 'department', minWidth: 120, flex: 1,
      valueGetter: (p) => p.data.employee.department,
      cellRenderer: ({ value }) => <span style={{ fontSize: 12, opacity: 0.85 }}>{value}</span>,
    },
    {
      headerName: 'Entry Type', field: 'entryType', minWidth: 130, flex: 1,
      cellRenderer: ({ value: type }) => {
        const icons   = { incentive: <FaGift />, advance: <FaHandHoldingUsd />, bonus: <FaTrophy />, deduction: <FaMinus />, recovery: <FaUndoAlt />, petty_cash: <FaWallet /> };
        const colors  = { incentive: '#4ADE80', advance: '#F59E0B', bonus: '#8660F8', deduction: '#EF4444', recovery: '#3B82F6', petty_cash: '#22D3EE' };
        const label   = (type || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        const color   = colors[type] || '#9B97B2';
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: `${color}22`, color, border: `1px solid ${color}44`,
            borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600,
          }}>
            {icons[type]} {label}
          </span>
        );
      },
    },
    {
      headerName: 'Reason', field: 'reason', minWidth: 160, flex: 1,
      cellRenderer: ({ value }) => <span style={{ fontSize: 12 }}>{value}</span>,
    },
    {
      headerName: 'Amount', field: 'amount', minWidth: 110, flex: 1,
      cellRenderer: ({ value, data }) => {
        const neg = data.entryType === 'deduction' || data.entryType === 'recovery';
        return (
          <span style={{ fontWeight: 600, color: neg ? '#EF4444' : '#4ADE80' }}>
            {neg ? '-' : '+'}{formatCurrency(value)}
          </span>
        );
      },
    },
    {
      headerName: 'Recovery Plan', field: 'recoveryPlan', minWidth: 130, flex: 1,
      cellRenderer: ({ value }) => <span style={{ fontSize: 12, opacity: 0.75 }}>{value}</span>,
    },
    {
      headerName: 'Date', field: 'date', minWidth: 100, flex: 1,
      cellRenderer: ({ value }) => <span style={{ fontSize: 12, opacity: 0.75 }}>{value}</span>,
    },
    {
      headerName: 'Status', field: 'status', minWidth: 110, flex: 1,
      cellRenderer: ({ value }) => {
        const map = { approved: ['#4ADE80', '#4ADE8022'], pending: ['#F59E0B', '#F59E0B22'], processed: ['#3B82F6', '#3B82F622'], recovered: ['#22D3EE', '#22D3EE22'], rejected: ['#EF4444', '#EF444422'] };
        const [color, bg] = map[value] || map.pending;
        return (
          <span style={{
            background: bg, color, border: `1px solid ${color}44`,
            borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600,
            display: 'inline-block',
          }}>
            {value ? value.charAt(0).toUpperCase() + value.slice(1) : 'Pending'}
          </span>
        );
      },
    },
    {
      headerName: 'Payroll Month', field: 'payrollMonth', minWidth: 110, flex: 1,
      cellRenderer: ({ value }) => <span style={{ fontSize: 12, opacity: 0.75 }}>{value}</span>,
    },
    {
      headerName: 'Actions', field: 'id', colId: 'actions',
      minWidth: 120, maxWidth: 130,
      sortable: false, filter: false, pinned: 'right',
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '0 6px' },
      // FIX: Use the row data passed in params to ensure latest callbacks fire
      cellRenderer: (params) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            title="Edit"
            onClick={(e) => { e.stopPropagation(); handleEditRow(params.data); }}
            style={btnStyle('#8660F8')}
          >
            <FaEdit size={11} />
          </button>
          <button
            title="Approve"
            onClick={(e) => { e.stopPropagation(); handleApproveRow(params.data.id); }}
            style={btnStyle('#4ADE80')}
          >
            <FaCheck size={11} />
          </button>
          <button
            title="Delete"
            onClick={(e) => { e.stopPropagation(); handleDeleteRow(params.data); }}
            style={btnStyle('#EF4444')}
          >
            <FaTrash size={11} />
          </button>
        </div>
      ),
    },
  ], [handleEditRow, handleApproveRow, handleDeleteRow]); // FIX: depend on handlers

  // FIX: defaultColDef includes flex so columns fill space and don't truncate
  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: false,
    suppressMovable: false,
    cellStyle: { display: 'flex', alignItems: 'center' },
  }), []);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="incentives-advances-container">
      <ToastContainer position="top-right" autoClose={2500} />

      <main className="main-content">
        {/* Page Header */}
        <header className="page-header">
          <div>
            <h1 className="page-title">Incentives &amp; Advances Center</h1>
            <p className="page-subtitle">
              Manage additions, recoveries, and employee adjustments before payroll run
              {monthLabel ? ` • ${monthLabel}` : ""}
            </p>
          </div>
        </header>

        {/* Filter & Action Bar */}
        <section className="filter-bar">
          <div className="filter-controls">
            {/* Month */}
            <div className="field-wrap field-month" style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", zIndex: 1, display: "flex", alignItems: "center", color: "var(--pay-text-muted, #888)" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </span>
              <input type="month" className="filter-select" value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                onClick={(e) => { try { e.target.showPicker(); } catch (_) {} }}
                style={{ paddingLeft: "28px", paddingRight: "10px", cursor: "pointer", colorScheme: "dark" }}
              />
            </div>
            {/* Branch */}
            <div className="field-wrap field-branch">
              <select className="filter-select" value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)}>
                <option value="">All Branches</option>
                {branchList.map((b) => <option key={b.branchId} value={b.branchId}>{b.branch_name}</option>)}
              </select>
              <span className="fi-right"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg></span>
            </div>
            {/* Department */}
            <div className="field-wrap field-dept">
              <select className="filter-select" value={selectedDepartment} onChange={(e) => setSelectedDepartment(e.target.value)}>
                <option value="">All Departments</option>
                {departmentList.map((d) => <option key={d.departmentId} value={d.departmentId}>{d.department_name}</option>)}
              </select>
              <span className="fi-right"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg></span>
            </div>
            {/* Status */}
            <div className="field-wrap field-status">
              <select className="filter-select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                <option value="all">All Status</option>
                {AI.statusOptions.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
              <span className="fi-right"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg></span>
            </div>
            {/* Search */}
            <div className="field-wrap field-search" style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--pay-text-muted, #888)' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              <input type="text" placeholder="Search employee..." className="filter-select"
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                style={{ paddingLeft: "26px", paddingRight: "10px", cursor: "text" }}
              />
            </div>
          </div>
          <div className="action-buttons">
            <button className="btn-ghost"><FaFileExcel className="mr-2" /> Import Excel</button>
            <button className="btn-ghost"><FaFileDownload className="mr-2" /> Export Report</button>
            <button className="btn-secondary" onClick={handleBulkApprove}><FaCheckDouble className="mr-2" /> Bulk Approve</button>
            <button type="button" className="btn-secondary" onClick={handleSendToPayroll} disabled={sendToPayrollLoading}>
              <FaPaperPlane className="mr-2" />
              {sendToPayrollLoading ? "Sending…" : "Send to Payroll"}
            </button>
            <button className="btn-primary" onClick={handleAddEntry}><FaPlus className="mr-2" /> Add New Entry</button>
          </div>
        </section>

        {/* KPI Cards */}
        <section className="kpi-grid">
          <div className="kpi-card kpi-success">
            <div className="kpi-icon"><FaGift /></div>
            <div className="kpi-value">{formatCurrency(kpis.totalIncentives)}</div>
            <div className="kpi-label">Total Incentives</div>
          </div>
          <div className="kpi-card kpi-warning">
          <div className="kpi-icon"><FaHandHoldingUsd /></div>
<div className="kpi-value">₹{kpis.totalAdvances.toLocaleString('en-IN')}</div>
<div className="kpi-label">Advances Issued</div>
          </div>
          <div className="kpi-card kpi-accent">
            <div className="kpi-icon"><FaClock /></div>
            <div className="kpi-value">{kpis.pendingApprovals}</div>
            <div className="kpi-label">Pending Approvals</div>
          </div>
          <div className="kpi-card kpi-blue">
         <div className="kpi-icon"><FaUndoAlt /></div>
<div className="kpi-value">₹{kpis.totalRecoveries.toLocaleString('en-IN')}</div>
<div className="kpi-label">Recoveries</div>
          </div>
          <div className="kpi-card kpi-purple">
            <div className="kpi-icon"><FaFileInvoiceDollar /></div>
            <div className="kpi-value">{kpis.activeAdvances}</div>
            <div className="kpi-label">Active Advances</div>
          </div>
          <div className="kpi-card kpi-error">
            <div className="kpi-icon"><FaExclamationTriangle /></div>
            <div className="kpi-value">{kpis.exceptions}</div>
            <div className="kpi-label">Exceptions</div>
          </div>
        </section>

        {/* Main Layout */}
        <div className="main-layout">
          {/* Left Column */}
          <div className="col-span-8">
            <div className="transaction-table">
              <div className="table-header">
                <h3 className="table-title">Employee Adjustments Register</h3>
                <div className="tab-buttons">
                  {['all','incentive','advance','recovery','bonus','deduction','petty-cash'].map((tab) => (
                    <button key={tab} className={`tab-button ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                      {tab === 'all' ? 'All' : tab === 'petty-cash' ? 'Petty Cash' : tab === 'deduction' ? 'Penalties' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>
                <div className="bulk-actions">
                  <label className="checkbox-label">
                    <input type="checkbox" className="checkbox-custom"
                      onChange={handleSelectAll}
                      checked={selectedRows.length === filteredTransactions.length && filteredTransactions.length > 0}
                    />
                    <span>Select All</span>
                  </label>
                  <button type="button" className="btn-ghost-small" onClick={handleBulkApprove}><FaCheckDouble /> Bulk Approve</button>
                  <button className="btn-ghost-small"><FaTimes /> Bulk Reject</button>
                  <button className="btn-ghost-small" onClick={handleMarkForPayroll}><FaFileInvoiceDollar /> Mark for Payroll</button>
                </div>
              </div>

              {/* FIX: Fixed height container so grid never collapses */}
              <div
                className="ag-theme-quartz-dark"
                style={{ width: '100%', minHeight: 400, height: Math.max(400, filteredTransactions.length * 44 + 80) }}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={filteredTransactions}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  onSelectionChanged={onSelectionChanged}
                  rowHeight={44}
                  headerHeight={38}
                  suppressCellFocus={true}
                  animateRows={true}
                  pagination={true}
                  paginationPageSize={10}
                  paginationPageSizeSelector={[10, 20, 50, 100]}
                  // FIX: use domLayout normal with explicit height so grid renders correctly
                  domLayout="normal"
                  overlayNoRowsTemplate='<span style="padding:20px;opacity:0.5;font-size:13px">No entries for the selected filters</span>'
                />
              </div>

              {/* Entry Type Distribution Donut */}
              {(() => {
                const CIRCUMFERENCE = 2 * Math.PI * 60;
                const COLORS = { incentive: '#4ADE80', advance: '#F59E0B', bonus: '#8660F8', deduction: '#EF4444', recovery: '#3B82F6', petty_cash: '#22D3EE' };
                const counts = {};
                (Array.isArray(transactions) ? transactions : []).forEach((t) => {
                  const key = (t.entryType || 'other').toLowerCase();
                  counts[key] = (counts[key] || 0) + 1;
                });
                const total    = Object.values(counts).reduce((s, v) => s + v, 0) || 1;
                const segments = Object.entries(counts)
                  .map(([key, count]) => ({ label: key.replace(/_/g,' ').replace(/\b\w/g,(c)=>c.toUpperCase()), count, pct: Math.round((count / total) * 100), color: COLORS[key] || '#9B97B2' }))
                  .sort((a, b) => b.count - a.count);
                let offset = 0;
                const arcs = segments.map((seg) => {
                  const dash = (seg.count / total) * CIRCUMFERENCE;
                  const arc  = { ...seg, dash, offset: -offset };
                  offset += dash;
                  return arc;
                });
                return (
                  <div className="chart-card" style={{ marginTop: 16 }}>
                    <div className="chart-card-title">Entry Type Distribution</div>
                    {segments.length === 0 ? (
                      <div style={{ padding: 24, textAlign: 'center', color: 'var(--pay-text-muted)', fontSize: 13 }}>No data for selected period</div>
                    ) : (
                      <div className="donut-wrap">
                        <div className="donut-svg-wrap">
                          <svg viewBox="0 0 160 160" className="donut-svg">
                            <circle cx="80" cy="80" r="60" fill="none" stroke="var(--pay-surface-2)" strokeWidth="20" />
                            {arcs.map((arc, i) => (
                              <circle key={i} cx="80" cy="80" r="60" fill="none"
                                stroke={arc.color} strokeWidth="20"
                                strokeDasharray={`${arc.dash.toFixed(1)} ${(CIRCUMFERENCE - arc.dash).toFixed(1)}`}
                                strokeDashoffset={arc.offset.toFixed(1)}
                              />
                            ))}
                          </svg>
                          <div className="donut-center-text">
                            <div className="donut-center-num">{total}</div>
                            <div className="donut-center-label">Total</div>
                          </div>
                        </div>
                        <div className="donut-legend">
                          {segments.map((seg) => (
                            <div key={seg.label} className="donut-legend-row">
                              <span className="chart-legend-dot" style={{ background: seg.color }} />
                              <span className="donut-legend-label">{seg.label}</span>
                              <span className="donut-legend-pct">{seg.pct}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Right Column — Quick Add Panel */}
          <div className="col-span-4">
            <div className="space-y-5">
              <div className="quick-add-card">
                <h3 className="card-title"><FaPlusCircle /> Add Adjustment</h3>
                <div className="card-body">
                  {/* Employee search */}
                  <div style={{ position: 'relative' }}>
                    <label className="form-label">Select Employee</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--pay-text-muted, #888)', display: 'flex', alignItems: 'center' }}>
                        <FaSearch style={{ fontSize: 12 }} />
                      </span>
                      <input type="text" className="form-select" style={{ paddingLeft: 30 }}
                        placeholder="Search employee..." value={employeeSearchTerm}
                        onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                      />
                    </div>
                    {employeeSearchTerm && filteredEmployees.length > 0 && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--pay-surface, #1a1040)', border: '1px solid var(--pay-border, #2d2060)', borderRadius: 8, maxHeight: 180, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
                        {filteredEmployees.map((emp) => (
                          <div key={emp.id || emp.staffId}
                            onClick={() => { handleEmployeeSelectForEntry(emp); setEmployeeSearchTerm(emp.name); }}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--pay-text, #fff)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(134,96,248,0.15)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontWeight: 600 }}>{emp.name}</span>
                            <span style={{ color: 'var(--pay-text-muted, #888)', marginLeft: 8, fontSize: 11 }}>{emp.id}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Entry Type */}
                  <div>
                    <label className="form-label">Entry Type</label>
                    <select className="form-select" value={selectedEntryType} onChange={(e) => setSelectedEntryType(e.target.value)}>
                      <option value="incentive">Incentive</option>
                      <option value="advance">Advance</option>
                      <option value="bonus">Bonus</option>
                      <option value="recovery">Recovery</option>
                      <option value="deduction">Deduction</option>
                      <option value="petty-cash">Petty Cash</option>
                    </select>
                  </div>

                  {selectedEntryType === 'incentive' && (
                    <div className="dynamic-fields">
                      <div>
                        <label className="form-label-sm">Incentive Category</label>
                        <select className="form-input-sm" value={incentiveData.category} onChange={(e) => setIncentiveData({ ...incentiveData, category: e.target.value })}>
                          {['Performance','Referral','Attendance','Festival','Sales','Custom'].map((o) => <option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="form-label-sm">Amount (₹)</label>
                        <input type="number" className="form-input-sm" placeholder="Enter amount" value={incentiveData.amount} onChange={(e) => setIncentiveData({ ...incentiveData, amount: e.target.value })} />
                      </div>
                      <div>
                        <label className="form-label-sm">Notes</label>
                        <textarea rows="2" className="form-textarea-sm" placeholder="Add notes..." value={incentiveData.description} onChange={(e) => setIncentiveData({ ...incentiveData, description: e.target.value })} />
                      </div>
                    </div>
                  )}

                  {selectedEntryType === 'advance' && (
                    <div className="dynamic-fields">
                      <div>
                        <label className="form-label-sm">Advance Category</label>
                        <select className="form-input-sm" value={advanceData.category} onChange={(e) => setAdvanceData({ ...advanceData, category: e.target.value })}>
                          {['Salary Advance','Emergency','Medical','Travel','Other'].map((o) => <option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="form-label-sm">Amount (₹)</label>
                        <input type="number" className="form-input-sm" placeholder="Enter amount" value={advanceData.amount} onChange={(e) => setAdvanceData({ ...advanceData, amount: e.target.value })} />
                      </div>
                      <div>
                        <label className="form-label-sm">Number of Installments</label>
                        <input type="number" className="form-input-sm" placeholder="e.g. 6" value={advanceData.installments} onChange={(e) => setAdvanceData({ ...advanceData, installments: e.target.value })} />
                      </div>
                      <div className="emi-preview">
                        <div className="emi-label">EMI Preview</div>
                        <div className="emi-value">₹{emiPreview.toFixed(0)} / month</div>
                      </div>
                    </div>
                  )}

                  {selectedEntryType === 'deduction' && (
                    <div className="dynamic-fields">
                      <div>
                        <label className="form-label-sm">Reason</label>
                        <input type="text" className="form-input-sm" placeholder="Enter reason" value={deductionData.type} onChange={(e) => setDeductionData({ ...deductionData, type: e.target.value })} />
                      </div>
                      <div>
                        <label className="form-label-sm">Amount (₹)</label>
                        <input type="number" className="form-input-sm" placeholder="Enter amount" value={deductionData.amount} onChange={(e) => setDeductionData({ ...deductionData, amount: e.target.value })} />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="form-label">Remarks</label>
                    <textarea rows="2" className="form-textarea" placeholder="Optional remarks..." value={internalComments} onChange={(e) => setInternalComments(e.target.value)} />
                  </div>

                  <div className="action-row">
                    <button className="btn-secondary flex-1"><FaSave /> Save Draft</button>
                    <button className="btn-primary flex-1" onClick={handleSubmitEntry}><FaPaperPlane /> Submit</button>
                  </div>
                </div>
              </div>

              {/* Employee Snapshot */}
              <div className="snapshot-card">
                <h4 className="snapshot-title"><FaUserCircle /> Employee Snapshot</h4>
                <div className="snapshot-employee">
                  <img src={selectedEmployeeDetails?.avatar} alt={selectedEmployeeDetails?.name} className="snapshot-avatar" />
                  <div>
                    <div className="snapshot-name">{selectedEmployeeDetails?.name || 'Select Employee'}</div>
                    <div className="snapshot-dept">{selectedEmployeeDetails?.department || '—'}</div>
                  </div>
                </div>
                <div className="snapshot-details">
                  <div className="detail-row"><span>Current Gross</span><span>{formatCurrency(resolveGrossSalary(selectedEmployeeDetails))}</span></div>
                  <div className="detail-row"><span>Incentives This Month</span><span className="success-text">{formatCurrency(selectedEmployeeDetails?.earnings?.incentiveAmount ?? 0)}</span></div>
                  <div className="detail-row"><span>Outstanding Advance</span><span className="warning-text">{formatCurrency(staffAdvanceSnapshot?.totalOutstanding ?? 0)}</span></div>
                  <div className="detail-row"><span>Net Impact</span><span className={netImpact.newAmount >= 0 ? 'success-text' : 'error-text'}>{formatSignedCurrency(netImpact.newAmount)}</span></div>
                  <div className="detail-row border-top">
                    <span>Payroll Status</span>
                    <span className={`status-chip ${selectedEmployeeDetails?.payrollStatus?.isFinalized ? 'status-pending' : 'status-approved'}`}>
                      {selectedEmployeeDetails?.payrollStatus?.isFinalized ? 'Finalized' : 'Ready'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* ── Full Modal ── */}
      {showAddModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); setEditingEntryId(null); }}>
          <div className="full-modal" onClick={(e) => e.stopPropagation()}>
            <div className="full-modal-header">
              <div>
                <h2 className="full-modal-title">{editingEntryId ? "Edit Adjustment" : "Add New Adjustment"}</h2>
                <p className="full-modal-sub">Create payroll adjustment for an employee</p>
              </div>
              <div className="full-modal-header-actions">
                <button className="btn-secondary"><FaSave /> Save Draft</button>
                <button className="btn-primary" onClick={handleSubmitEntry}><FaPaperPlane /> Submit</button>
                <button className="full-modal-close" onClick={() => { setShowAddModal(false); setEditingEntryId(null); }}><FaTimes /></button>
              </div>
            </div>

            <div className="full-modal-body">
              {/* LEFT */}
              <div className="full-modal-left">
                {/* Step 1: Employee Selection */}
                <div className="modal-section">
                  <h4 className="section-title"><span className="step-badge">1</span> Employee Selection</h4>
                  <div className="employee-search-wrapper">
                    <FaSearch className="search-icon-inline" />
                    <input type="text" className="employee-search-input"
                      placeholder="Search by name or ID..."
                      value={employeeSearchTerm}
                      onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* Employee list — always visible when search is filled */}
                  {filteredEmployees.length > 0 && (
                    <div className="employee-list" style={{ maxHeight: 200, overflowY: 'auto' }}>
                      {filteredEmployees.slice(0, 20).map((emp) => (
                        <div key={emp.id || emp.staffId}
                          className={`employee-item ${selectedEmployeeForEntry && Number(selectedEmployeeForEntry.staffId) === Number(emp.staffId) ? 'selected' : ''}`}
                          onClick={() => handleEmployeeSelectForEntry(emp)}
                        >
                          <img src={emp.avatar} alt={emp.name} className="employee-avatar-md" />
                          <div className="employee-info">
                            <div className="font-semibold">{emp.name}</div>
                            <div className="employee-meta">{emp.id} · {emp.department} · {emp.designation}</div>
                          </div>
                          {selectedEmployeeForEntry && Number(selectedEmployeeForEntry.staffId) === Number(emp.staffId) && <FaCheck className="check-icon" />}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Selected employee strip */}
                  {selectedEmployeeForEntry && (
                    <div className="selected-employee-strip">
                      <img src={selectedEmployeeForEntry.avatar} alt={selectedEmployeeForEntry.name} className="selected-avatar" />
                      <div className="selected-details">
                        <div><span>Name</span><div>{selectedEmployeeForEntry.name}</div></div>
                        <div><span>Emp ID</span><div>{selectedEmployeeForEntry.id}</div></div>
                        <div><span>Department</span><div>{selectedEmployeeForEntry.department}</div></div>
                        <div><span>Designation</span><div>{selectedEmployeeForEntry.designation}</div></div>
                        <div><span>Branch</span><div>{selectedEmployeeForEntry.branch}</div></div>
                        <div><span>Monthly Gross</span><div className="success-text">{formatCurrency(resolveGrossSalary(selectedEmployeeForEntry))}</div></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Step 2: Entry Type */}
                <div className="modal-section">
                  <h4 className="section-title"><span className="step-badge">2</span> Select Adjustment Type</h4>
                  <div className="type-buttons">
                    {[
                      { key: 'incentive',  icon: <FaGift />,          label: 'Incentive'  },
                      { key: 'advance',    icon: <FaHandHoldingUsd />, label: 'Advance'    },
                      { key: 'bonus',      icon: <FaTrophy />,         label: 'Bonus'      },
                      { key: 'recovery',   icon: <FaUndoAlt />,        label: 'Recovery'   },
                      { key: 'deduction',  icon: <FaMinusCircle />,    label: 'Deduction'  },
                      { key: 'petty-cash', icon: <FaWallet />,         label: 'Petty Cash' },
                    ].map((t) => (
                      <button key={t.key} className={`type-btn ${selectedEntryType === t.key ? 'active' : ''}`}
                        onClick={() => setSelectedEntryType(t.key)}>
                        {t.icon}{t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Step 3: Details */}
                <div className="modal-section">
                  <h4 className="section-title"><span className="step-badge">3</span> Adjustment Details</h4>

                  {selectedEntryType === 'incentive' && (
                    <>
                      <div className="form-grid">
                        <div><label>Incentive Category</label>
                          <select value={incentiveData.category} onChange={(e) => setIncentiveData({ ...incentiveData, category: e.target.value })}>
                            {AI.incentiveCategories.map((o) => <option key={o}>{o}</option>)}
                          </select>
                        </div>
                        <div><label>Amount (₹)</label>
                          <input type="number" placeholder="Enter amount" value={incentiveData.amount} onChange={(e) => setIncentiveData({ ...incentiveData, amount: e.target.value })} />
                        </div>
                        <div><label>Effective Month</label>
                          <select value={incentiveData.effectiveMonth} onChange={(e) => setIncentiveData({ ...incentiveData, effectiveMonth: e.target.value })}>
                            {['April 2026','May 2026','June 2026'].map((o) => <option key={o}>{o}</option>)}
                          </select>
                        </div>
                        <div><label>Recurring</label>
                          <select value={incentiveData.recurring} onChange={(e) => setIncentiveData({ ...incentiveData, recurring: e.target.value })}>
                            {['One Time','Monthly','Quarterly'].map((o) => <option key={o}>{o}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="toggle-item mt-3">
                        <span>Taxable</span>
                        <label className="toggle-switch"><input type="checkbox" checked={incentiveData.taxable} onChange={(e) => setIncentiveData({ ...incentiveData, taxable: e.target.checked })} /><span className="toggle-slider" /></label>
                      </div>
                      <div className="mt-3"><label>Description / Reason</label>
                        <textarea rows="3" placeholder="Provide reason..." value={incentiveData.description} onChange={(e) => setIncentiveData({ ...incentiveData, description: e.target.value })} />
                      </div>
                    </>
                  )}

                  {selectedEntryType === 'advance' && (
                    <>
                      <div className="form-grid">
                        <div><label>Advance Category</label>
                          <select value={advanceData.category} onChange={(e) => setAdvanceData({ ...advanceData, category: e.target.value })}>
                            {AI.advanceCategories.map((o) => <option key={o}>{o}</option>)}
                          </select>
                        </div>
                        <div><label>Amount (₹)</label>
                          <input type="number" placeholder="Enter amount" value={advanceData.amount} onChange={(e) => setAdvanceData({ ...advanceData, amount: e.target.value })} />
                        </div>
                        <div><label>Disbursement Date</label>
                          <input type="date" value={advanceData.disbursementDate} onChange={(e) => setAdvanceData({ ...advanceData, disbursementDate: e.target.value })} />
                        </div>
                        <div><label>Recovery Start Month</label>
                          <select value={advanceData.recoveryStartMonth} onChange={(e) => setAdvanceData({ ...advanceData, recoveryStartMonth: e.target.value })}>
                            {['May 2026','June 2026','July 2026'].map((o) => <option key={o}>{o}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="mt-3"><label>Recovery Method</label>
                        <div className="modal-radio-group">
                          <label className="modal-radio-label"><input type="radio" name="rm" value="emi" checked={advanceData.recoveryMethod === 'emi'} onChange={(e) => setAdvanceData({ ...advanceData, recoveryMethod: e.target.value })} />EMI Installments</label>
                          <label className="modal-radio-label"><input type="radio" name="rm" value="full" checked={advanceData.recoveryMethod === 'full'} onChange={(e) => setAdvanceData({ ...advanceData, recoveryMethod: e.target.value })} />Full Next Salary</label>
                        </div>
                      </div>
                      {advanceData.recoveryMethod === 'emi' && (
                        <div className="form-grid mt-3">
                          <div><label>Installments</label>
                            <input type="number" placeholder="e.g. 6" value={advanceData.installments} onChange={(e) => setAdvanceData({ ...advanceData, installments: e.target.value })} />
                          </div>
                          <div><label>EMI Amount (Auto)</label>
                            <input type="text" readOnly value={emiPreview > 0 ? `₹${emiPreview.toFixed(0)} / month` : ''} placeholder="Auto calculated" />
                          </div>
                        </div>
                      )}
                      <div className="form-grid mt-3">
                        <div className="toggle-item"><span>Allow Early Closure</span><label className="toggle-switch"><input type="checkbox" checked={advanceData.allowEarlyClosure} onChange={(e) => setAdvanceData({ ...advanceData, allowEarlyClosure: e.target.checked })} /><span className="toggle-slider" /></label></div>
                        <div className="toggle-item"><span>Approval Required</span><label className="toggle-switch"><input type="checkbox" checked={advanceData.approvalRequired} onChange={(e) => setAdvanceData({ ...advanceData, approvalRequired: e.target.checked })} /><span className="toggle-slider" /></label></div>
                      </div>
                      <div className="mt-3"><label>Notes</label>
                        <textarea rows="2" placeholder="Add notes..." value={advanceData.notes} onChange={(e) => setAdvanceData({ ...advanceData, notes: e.target.value })} />
                      </div>
                    </>
                  )}

                  {selectedEntryType === 'bonus' && (
                    <>
                      <div className="form-grid">
                        <div><label>Bonus Type</label>
                          <select value={bonusData.type} onChange={(e) => setBonusData({ ...bonusData, type: e.target.value })}>
                            {AI.bonusTypes.map((o) => <option key={o}>{o}</option>)}
                          </select>
                        </div>
                        <div><label>Amount (₹)</label>
                          <input type="number" placeholder="Enter amount" value={bonusData.amount} onChange={(e) => setBonusData({ ...bonusData, amount: e.target.value })} />
                        </div>
                      </div>
                      <div className="mt-3"><label>Effective Month</label>
                        <select value={bonusData.effectiveMonth} onChange={(e) => setBonusData({ ...bonusData, effectiveMonth: e.target.value })}>
                          {['April 2026','May 2026','June 2026'].map((o) => <option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div className="mt-3"><label>Notes</label>
                        <textarea rows="2" placeholder="Add notes..." value={bonusData.notes} onChange={(e) => setBonusData({ ...bonusData, notes: e.target.value })} />
                      </div>
                    </>
                  )}

                  {selectedEntryType === 'deduction' && (
                    <>
                      <div className="form-grid">
                        <div><label>Deduction Type</label>
                          <select value={deductionData.type} onChange={(e) => setDeductionData({ ...deductionData, type: e.target.value })}>
                            {AI.deductionTypes.map((o) => <option key={o}>{o}</option>)}
                          </select>
                        </div>
                        <div><label>Amount (₹)</label>
                          <input type="number" placeholder="Enter amount" value={deductionData.amount} onChange={(e) => setDeductionData({ ...deductionData, amount: e.target.value })} />
                        </div>
                      </div>
                      <div className="mt-3"><label>Recovery Mode</label>
                        <select value={deductionData.recoveryMode} onChange={(e) => setDeductionData({ ...deductionData, recoveryMode: e.target.value })}>
                          {['Current Payroll (Full)','Installments'].map((o) => <option key={o}>{o}</option>)}
                        </select>
                      </div>
                      <div className="mt-3"><label>Remarks</label>
                        <textarea rows="2" placeholder="Add remarks..." value={deductionData.remarks} onChange={(e) => setDeductionData({ ...deductionData, remarks: e.target.value })} />
                      </div>
                    </>
                  )}

                  {selectedEntryType === 'recovery' && (
                    <>
                      <div className="mt-3"><label>Select Existing Advance Case</label>
                        <select value={recoveryData.advanceCase} onChange={(e) => setRecoveryData({ ...recoveryData, advanceCase: e.target.value })}>
                          <option value="">Select advance</option>
                          {(staffAdvanceSnapshot?.advances || []).map((advance) => (
                            <option key={advance.id} value={`ADV-${advance.id}`}>
                              {`ADV-${advance.id} — ${advance.type || 'Advance'} ${formatCurrency(advance.amount)}`}
                            </option>
                          ))}
                          {false && (
                            <>
                          <option value="ADV-2024-001">ADV-2024-001 — Emergency Advance ₹15,000</option>
                          <option value="ADV-2024-002">ADV-2024-002 — Medical Advance ₹8,000</option>
                            </>
                          )}
                        </select>
                      </div>
                      <div className="form-grid mt-3">
                        <div><label>Recovery This Month (₹)</label>
                          <input type="number" placeholder="Enter amount" value={recoveryData.recoveryAmount} onChange={(e) => setRecoveryData({ ...recoveryData, recoveryAmount: e.target.value })} />
                        </div>
                        <div><label>Remaining Balance (₹)</label>
                          <input type="text" readOnly value={recoveryData.recoveryAmount ? formatCurrency(Math.max(Number(staffAdvanceSnapshot?.totalOutstanding || 0) - parseFloat(recoveryData.recoveryAmount || 0), 0)) : formatCurrency(staffAdvanceSnapshot?.totalOutstanding ?? 0)} placeholder="Auto calculated" />
                        </div>
                      </div>
                    </>
                  )}

                  {selectedEntryType === 'petty-cash' && (
                    <>
                      <div className="form-grid">
                        <div><label>Petty Cash Type</label>
                          <select value={pettyCashData.purpose} onChange={(e) => setPettyCashData({ ...pettyCashData, purpose: e.target.value })}>
                            <option value="">Select type</option>
                            {['Office Supplies','Travel & Conveyance','Meals & Entertainment','Courier & Postage','Miscellaneous'].map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        <div><label>Amount (₹)</label>
                          <input type="number" placeholder="Enter amount" value={pettyCashData.amount} onChange={(e) => setPettyCashData({ ...pettyCashData, amount: e.target.value })} />
                        </div>
                      </div>
                      <div className="mt-3"><label>Remarks</label>
                        <input type="text" placeholder="Add remarks..." value={pettyCashData.notes} onChange={(e) => setPettyCashData({ ...pettyCashData, notes: e.target.value })} />
                      </div>
                    </>
                  )}
                </div>

                {/* Internal Comments */}
                <div className="modal-section">
                  <h4 className="section-title">Internal Comments</h4>
                  <textarea rows="3" placeholder="Add internal notes for HR/Payroll team..." value={internalComments} onChange={(e) => setInternalComments(e.target.value)} />
                </div>

                <div className="full-modal-footer">
                  <button className="btn-ghost" onClick={() => { setShowAddModal(false); setEditingEntryId(null); }}>Cancel</button>
                  <button className="btn-secondary"><FaSave /> Save Draft</button>
                  <button className="btn-primary" onClick={handleSubmitEntry}><FaPaperPlane /> Submit for Approval</button>
                </div>
              </div>

              {/* RIGHT SIDEBAR */}
              <div className="full-modal-right">
                {/* Employee Payroll Snapshot */}
                <div className="modal-sidebar-card">
                  <div className="modal-sidebar-title"><FaUserCircle /> Employee Payroll Snapshot</div>
                  {payrollEarningLoading && (
                    <div style={{ padding: '12px 0', textAlign: 'center', fontSize: 13, opacity: 0.7 }}>Loading payroll data…</div>
                  )}
                  {selectedEmployeeForEntry ? (
                    <>
                      <div className="modal-emp-info">
                        <img src={selectedEmployeeForEntry.avatar} alt={selectedEmployeeForEntry.name} className="snapshot-avatar" />
                        <div>
                          <div className="snapshot-name">{selectedEmployeeForEntry.name}</div>
                          <div className="snapshot-dept">{selectedEmployeeForEntry.department}</div>
                        </div>
                      </div>
                      <div className="snapshot-details">
                        {(() => {
                          const snapshotData = selectedEmployeeForEntry?.snapshotData || {};
                          const currentGross = snapshotData.currentGross ?? resolveGrossSalary(selectedEmployeeForEntry);
                          const currentNet   = snapshotData.currentNet   ?? resolveNetSalary(selectedEmployeeForEntry);
                          const incentivesThisMonth = snapshotData.last3MonthsIncentives ?? 0;
                          const outstandingAdvance  = snapshotData.outstandingAdvance ?? staffAdvanceSnapshot?.totalOutstanding ?? 0;
                          const currentDeductions   = selectedEmployeeForEntry?.deductions ?? 0;
                          const payrollLock         = snapshotData.payrollStatus || "Open";

                          const incDelta = selectedEntryType === 'incentive' ? (parseFloat(incentiveData.amount) || 0) : selectedEntryType === 'bonus' ? (parseFloat(bonusData.amount) || 0) : 0;
                          const dedDelta = selectedEntryType === 'deduction' ? (parseFloat(deductionData.amount) || 0) : 0;

                          const rows = [
                            ['Current Gross',         formatCurrency(currentGross),                           ''],
                            ['Current Net',            formatCurrency(currentNet),                             ''],
                            ['Incentives This Month',  formatSignedCurrency(incentivesThisMonth + incDelta),   'success-text'],
                            ['Outstanding Advance',    formatCurrency(outstandingAdvance),                     'warning-text'],
                            ['Deductions',             `-${formatCurrency(currentDeductions + dedDelta)}`,     'error-text'],
                            ['Payroll Lock',           payrollLock,                                            payrollLock === 'Locked' ? 'warning-text' : 'success-text'],
                          ];
                          return rows.map(([l, v, c]) => (
                            <div key={l} className="detail-row"><span>{l}</span><span className={c}>{v}</span></div>
                          ));
                        })()}
                      </div>
                    </>
                  ) : (
                    <div className="modal-empty-state">Select an employee to see snapshot</div>
                  )}
                </div>

                {/* Policy Checks */}
                <div className="modal-sidebar-card">
                  <div className="modal-sidebar-title"><FaExclamationTriangle /> Policy Checks</div>
                  <div className="policy-list">
                    <div className="policy-item policy-warning">
                      <FaExclamationTriangle className="policy-icon warning-text" />
                      <div><div className="policy-title">Manager Approval Required</div><div className="policy-desc">Incentive exceeds auto-approval limit of {formatCurrency(2500)}</div></div>
                    </div>
                    <div className="policy-item policy-warning">
                      <FaExclamationTriangle className="policy-icon warning-text" />
                      <div><div className="policy-title">Active Advance Detected</div><div className="policy-desc">Employee has {formatCurrency(selectedEmployeeForEntry?.snapshotData?.outstandingAdvance ?? 0)} outstanding</div></div>
                    </div>
                    <div className="policy-item policy-success">
                      <FaCheckCircle className="policy-icon success-text" />
                      <div><div className="policy-title">Eligible for Festival Bonus</div><div className="policy-desc">Employee completed 1 year tenure</div></div>
                    </div>
                  </div>
                </div>

                {/* Net Impact */}
                {selectedEmployeeForEntry && (
                  <div className="modal-sidebar-card">
                    <div className="modal-sidebar-title"><FaChartLine /> Net Impact Preview</div>
                    <div style={{ padding: '8px 0' }}>
                      <div className="detail-row"><span>Current Net</span><span>{formatCurrency(netImpact.currentNet)}</span></div>
                      <div style={{ textAlign: 'center', fontSize: 20, fontWeight: 700, padding: '8px 0' }}>
                        <span className={netImpact.newAmount >= 0 ? 'success-text' : 'error-text'}>{formatSignedCurrency(netImpact.newAmount)}</span>
                      </div>
                      <div className="detail-row" style={{ borderTop: '1px solid var(--pay-border)', paddingTop: 8 }}>
                        <span>Projected Net</span>
                        <span style={{ fontWeight: 700, color: 'var(--pay-accent)' }}>{formatCurrency(netImpact.projectedNet)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bottom Status Bar */}
      <div className="bottom-status-bar">
        <div className="status-left">
          <div className="status-label">Payroll Readiness</div>
          <div className="status-stats">
            <span className="stat"><FaFileInvoiceDollar /> <strong>{transactions.length}</strong> Entries</span>
            <span className="stat"><FaCheckCircle /> <strong>{transactions.filter((t) => t.status === 'approved').length}</strong> Approved</span>
            <span className="stat"><FaClock /> <strong>{transactions.filter((t) => t.status === 'pending').length}</strong> Pending</span>
            <span className="stat"><FaExclamationCircle /> <strong>{transactions.filter((t) => t.status === 'rejected').length}</strong> Errors</span>
          </div>
        </div>
        <div className="progress-container">
          {(() => {
            const approved = transactions.filter((t) => t.status === 'approved' || t.status === 'processed').length;
            const pct = transactions.length ? Math.round((approved / transactions.length) * 100) : 0;
            return (
              <>
                <div className="progress-label"><span>Completion</span><span className="success-text">{pct}%</span></div>
                <div className="progress-bar-bg"><div className="progress-bar-fill" style={{ width: `${pct}%` }} /></div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default IncentivesAdvances;

// ── Tiny inline helper for action button styles (avoids CSS dependency) ──────
function btnStyle(color) {
  return {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 26, height: 26, borderRadius: 6, border: `1px solid ${color}55`,
    background: `${color}18`, color, cursor: 'pointer', transition: 'all 0.15s',
    flexShrink: 0,
  };
}
